import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from 'firebase/auth'
import {
  signInWithProvider,
  type ProviderId,
  signOutUser,
  onAuthStateChange,
  configError,
} from '../lib/firebase'
import { onSessionExpired, SESSION_EXPIRED } from '../lib/api'

export type AuthStatus =
  | 'firebase-not-configured'
  | 'loading'
  | 'signed-out'
  | 'signed-in'

interface AuthContextType {
  status: AuthStatus
  user: User | null
  error: Error | null
  signIn: (provider: ProviderId) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [error, setError] = useState<Error | null>(configError)

  useEffect(() => {
    // If Firebase is not configured, show that error state
    if (configError) {
      setStatus('firebase-not-configured')
      return
    }

    // Listen for auth state changes
    let unsubscribe: (() => void) | undefined

    try {
      unsubscribe = onAuthStateChange((firebaseUser) => {
        setUser(firebaseUser)
        setStatus(firebaseUser ? 'signed-in' : 'signed-out')
        // A sign-in that worked retires an earlier message, so a later sign-out does not repeat it.
        if (firebaseUser) setError(null)
      })
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      setStatus('signed-out')
    }

    // A request found the sign-in can no longer be renewed: sign out, so the
    // sign-in screen replaces whatever screen was open, and say why there.
    let expiring = false
    const stopExpiry = onSessionExpired(() => {
      if (expiring) return
      expiring = true
      setError(new Error(SESSION_EXPIRED))
      setUser(null)
      setStatus('signed-out')
      signOutUser()
        .catch(() => {})
        .finally(() => {
          expiring = false
        })
    })

    return () => {
      stopExpiry()
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [])

  const handleSignIn = async (provider: ProviderId) => {
    try {
      setStatus('loading')
      await signInWithProvider(provider)
    } catch (err) {
      const errorMsg = err instanceof Error ? err : new Error(String(err))
      setError(errorMsg)
      setStatus('signed-out')
      throw errorMsg
    }
  }

  const handleSignOut = async () => {
    try {
      await signOutUser()
      setUser(null)
      setStatus('signed-out')
    } catch (err) {
      const errorMsg = err instanceof Error ? err : new Error(String(err))
      setError(errorMsg)
      throw errorMsg
    }
  }

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        error,
        signIn: handleSignIn,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
